import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { ThemedText } from '../components';
import { Mail, MessageSquare, ClipboardList, Wrench, FileText, Lock, Settings, Bell } from 'lucide-react';
import notificationEventService from '../api/services/notificationEventService';

// --- Animations ---

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// --- Constants ---

const RECIPIENT_LABELS = {
  Submitter: 'Submitter',
  AssignedUser: 'Assigned User',
  Customer: 'Customer',
  OrgAdmins: 'Org Admins',
  RequestingUser: 'Requesting User',
};

const CHANNEL_LABELS = {
  Email: 'Email',
  SMS: 'SMS',
};

const CHANNEL_ICONS = {
  Email: <Mail size={14} />,
  SMS: <MessageSquare size={14} />,
};

const CATEGORY_ORDER = ['Submissions', 'WorkOrders', 'Reports', 'Authentication', 'System'];

const CATEGORY_META = {
  Submissions: { icon: <ClipboardList size={18} />, color: '#3b82f6', label: 'Submissions' },
  WorkOrders: { icon: <Wrench size={18} />, color: '#f59e0b', label: 'Work Orders' },
  Reports: { icon: <FileText size={18} />, color: '#8b5cf6', label: 'Reports' },
  Authentication: { icon: <Lock size={18} />, color: '#10b981', label: 'Authentication' },
  System: { icon: <Settings size={18} />, color: '#6b7280', label: 'System' },
};

// --- Styled Components ---

const PageContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
  animation: ${fadeInUp} 0.4s ease-out;
`;

const PageHeader = styled.div`
  margin-bottom: 28px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 8px;
`;

const HeaderIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #59caee18, #59caee08);
  border: 1px solid ${p => p.theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
`;

const HeaderStats = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
`;

const StatBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 20px;
  background: ${p => p.$bg || p.theme.colors.border + '30'};
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.$color || p.theme.colors.text};
`;

const StatDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${p => p.$color || '#59caee'};
`;

const PageDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${p => p.theme.colors.muted};
  line-height: 1.6;
`;

// --- Category ---

const CategorySection = styled.div`
  margin-bottom: 32px;
  animation: ${fadeInUp} 0.5s ease-out ${p => p.$delay || '0s'} both;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const CategoryIcon = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${p => p.$color}14;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
`;

const CategoryTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
  margin: 0;
  flex: 1;
`;

const CategoryCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: ${p => p.$color}14;
  color: ${p => p.$color};
`;

// --- Event Card ---

const EventCard = styled.div`
  background: ${p => p.theme.colors.backgroundSecondary || p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-left: 3px solid ${p => p.$accent || p.theme.colors.border};
  border-radius: 10px;
  padding: 18px 22px;
  margin-bottom: 10px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    border-left-color: ${p => p.$accent || p.theme.colors.tint};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const EventHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const EventTitle = styled.h4`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
`;

const SystemBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 12px;
  background: ${p => p.theme.colors.muted}15;
  color: ${p => p.theme.colors.muted};
  border: 1px solid ${p => p.theme.colors.border};
  letter-spacing: 0.2px;
`;

const EventDescription = styled.p`
  margin: 0 0 14px 0;
  font-size: 13px;
  color: ${p => p.theme.colors.muted};
  line-height: 1.5;
`;

// --- Rule Grid ---

const RuleGridContainer = styled.div`
  background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 8px;
  overflow: hidden;
`;

const RuleGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr repeat(${p => p.$cols}, 90px);
  gap: 0;
  align-items: center;
`;

const GridHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1fr repeat(${p => p.$cols}, 90px);
  background: ${p => p.theme.colors.border}28;
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const GridHeaderCell = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${p => p.theme.colors.muted};
  padding: 8px 14px;
  text-align: ${p => p.$align || 'left'};
  display: flex;
  align-items: center;
  justify-content: ${p => p.$align === 'center' ? 'center' : 'flex-start'};
  gap: 5px;
`;

const ChannelIcon = styled.span`
  font-size: 13px;
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: 1fr repeat(${p => p.$cols}, 90px);
  border-bottom: 1px solid ${p => p.theme.colors.border}60;
  transition: background-color 0.1s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${p => p.theme.colors.tint}06;
  }
`;

const GridCell = styled.div`
  padding: 10px 14px;
  text-align: ${p => p.$align || 'left'};
  font-size: 13px;
  color: ${p => p.theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: ${p => p.$align === 'center' ? 'center' : 'flex-start'};
`;

const RecipientLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
`;

// --- Toggle ---

const ToggleSwitch = styled.button`
  width: 38px;
  height: 22px;
  border-radius: 11px;
  border: none;
  cursor: pointer;
  position: relative;
  transition: background-color 0.25s ease;
  background-color: ${p => p.$enabled ? (p.theme.colors.success || '#22c55e') : p.theme.colors.border};
  outline: none;

  &:hover {
    box-shadow: 0 0 0 3px ${p => p.$enabled ? (p.theme.colors.success || '#22c55e') + '25' : p.theme.colors.border + '40'};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${p => p.$enabled ? '19px' : '3px'};
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: white;
    transition: left 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }
`;

const NotAvailable = styled.span`
  font-size: 12px;
  color: ${p => p.theme.colors.border};
`;

const ResetButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 6px;
  border: 1px solid ${p => p.theme.colors.border};
  background: transparent;
  color: ${p => p.theme.colors.muted};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${p => p.theme.colors.border}30;
    color: ${p => p.theme.colors.text};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ConfirmDialog = styled.div`
  background: ${p => p.theme.colors.backgroundSecondary || p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 12px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
`;

const ConfirmTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
`;

const ConfirmText = styled.p`
  margin: 0 0 20px 0;
  font-size: 13px;
  color: ${p => p.theme.colors.muted};
  line-height: 1.5;
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ConfirmCancelBtn = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid ${p => p.theme.colors.border};
  background: transparent;
  color: ${p => p.theme.colors.text};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: ${p => p.theme.colors.border}30;
  }
`;

const ConfirmResetBtn = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  background: ${p => p.theme.colors.tint || '#59caee'};
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// --- Loading ---

const SkeletonCard = styled.div`
  background: ${p => p.theme.colors.backgroundSecondary || p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 10px;
  padding: 18px 22px;
  margin-bottom: 10px;
`;

const SkeletonLine = styled.div`
  height: ${p => p.$h || '14px'};
  width: ${p => p.$w || '100%'};
  border-radius: 6px;
  background: linear-gradient(90deg, ${p => p.theme.colors.border}40 25%, ${p => p.theme.colors.border}20 50%, ${p => p.theme.colors.border}40 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s infinite linear;
  margin-bottom: ${p => p.$mb || '0'};
`;

const LoadingSkeleton = ({ theme }) => (
  <>
    {[1, 2, 3].map(i => (
      <SkeletonCard key={i} theme={theme}>
        <SkeletonLine theme={theme} $w="40%" $h="16px" $mb="10px" />
        <SkeletonLine theme={theme} $w="70%" $h="12px" $mb="14px" />
        <SkeletonLine theme={theme} $w="100%" $h="60px" />
      </SkeletonCard>
    ))}
  </>
);

// --- Empty State ---

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${p => p.theme.colors.muted};
`;

const EmptyIcon = styled.div`
  font-size: 40px;
  margin-bottom: 12px;
  opacity: 0.5;
`;

// --- Component ---

export const NotificationEventsSection = () => {
  const theme = useTheme();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetConfirm, setResetConfirm] = useState(null);
  const [resetting, setResetting] = useState(false);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await notificationEventService.getAll();
      setEvents(data);
    } catch (err) {
      toast.error('Failed to load notification events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const grouped = useMemo(() => {
    const groups = {};
    events.forEach(evt => {
      if (!groups[evt.category]) groups[evt.category] = [];
      groups[evt.category].push(evt);
    });
    return groups;
  }, [events]);

  const sortedCategories = useMemo(() => {
    const cats = Object.keys(grouped);
    return CATEGORY_ORDER.filter(c => cats.includes(c)).concat(cats.filter(c => !CATEGORY_ORDER.includes(c)));
  }, [grouped]);

  const stats = useMemo(() => {
    const configurable = events.filter(e => e.isUserConfigurable).length;
    const categories = new Set(events.map(e => e.category)).size;
    return { total: events.length, configurable, categories };
  }, [events]);

  const handleToggle = async (eventType, channel, recipientType, currentValue) => {
    setEvents(prev => prev.map(evt => {
      if (evt.eventType !== eventType) return evt;
      const updatedRules = { ...evt.rules };
      if (updatedRules[channel]) {
        updatedRules[channel] = { ...updatedRules[channel], [recipientType]: !currentValue };
      }
      return { ...evt, rules: updatedRules };
    }));

    try {
      const evt = events.find(e => e.eventType === eventType);
      if (!evt) return;

      const updatedRules = { ...evt.rules };
      if (updatedRules[channel]) {
        updatedRules[channel] = { ...updatedRules[channel], [recipientType]: !currentValue };
      }

      const rules = [];
      Object.entries(updatedRules).forEach(([ch, recipients]) => {
        Object.entries(recipients).forEach(([rt, enabled]) => {
          rules.push({ channel: ch, recipientType: rt, isEnabled: enabled });
        });
      });

      await notificationEventService.updateRules(eventType, rules);
    } catch (err) {
      setEvents(prev => prev.map(evt => {
        if (evt.eventType !== eventType) return evt;
        const rolledBack = { ...evt.rules };
        if (rolledBack[channel]) {
          rolledBack[channel] = { ...rolledBack[channel], [recipientType]: currentValue };
        }
        return { ...evt, rules: rolledBack };
      }));
      toast.error('Failed to update notification rule');
    }
  };

  const handleResetToDefault = async (eventType) => {
    setResetting(true);
    try {
      await notificationEventService.resetToDefault(eventType);
      // Reload the single event to get system defaults
      const updated = await notificationEventService.getByEventType(eventType);
      setEvents(prev => prev.map(evt => evt.eventType === eventType ? updated : evt));
      toast.success('Restored to system defaults');
    } catch (err) {
      toast.error('Failed to restore defaults');
    } finally {
      setResetting(false);
      setResetConfirm(null);
    }
  };

  const renderRuleGrid = (evt) => {
    const channels = Object.keys(evt.rules);
    if (!evt.isUserConfigurable || channels.length === 0) return null;

    const allRecipients = new Set();
    channels.forEach(ch => {
      Object.keys(evt.rules[ch]).forEach(r => allRecipients.add(r));
    });
    const recipients = Array.from(allRecipients);

    return (
      <RuleGridContainer theme={theme}>
        <GridHeaderRow theme={theme} $cols={channels.length}>
          <GridHeaderCell theme={theme}>Recipient</GridHeaderCell>
          {channels.map(ch => (
            <GridHeaderCell key={ch} theme={theme} $align="center">
              <ChannelIcon>{CHANNEL_ICONS[ch] || ''}</ChannelIcon>
              {CHANNEL_LABELS[ch] || ch}
            </GridHeaderCell>
          ))}
        </GridHeaderRow>

        {recipients.map(recipient => (
          <GridRow key={recipient} theme={theme} $cols={channels.length}>
            <GridCell theme={theme}>
              <RecipientLabel>{RECIPIENT_LABELS[recipient] || recipient}</RecipientLabel>
            </GridCell>
            {channels.map(ch => {
              const isAvailable = evt.rules[ch] && recipient in evt.rules[ch];
              const isEnabled = isAvailable ? evt.rules[ch][recipient] : false;

              return (
                <GridCell key={ch} theme={theme} $align="center">
                  {isAvailable ? (
                    <ToggleSwitch
                      theme={theme}
                      $enabled={isEnabled}
                      onClick={() => handleToggle(evt.eventType, ch, recipient, isEnabled)}
                      title={`${isEnabled ? 'Disable' : 'Enable'} ${CHANNEL_LABELS[ch]} for ${RECIPIENT_LABELS[recipient] || recipient}`}
                    />
                  ) : (
                    <NotAvailable theme={theme}>&mdash;</NotAvailable>
                  )}
                </GridCell>
              );
            })}
          </GridRow>
        ))}
      </RuleGridContainer>
    );
  };

  const renderEventCard = (evt, categoryColor) => (
    <EventCard key={evt.eventType} theme={theme} $accent={categoryColor}>
      <EventHeaderRow>
        <EventTitle theme={theme}>{evt.displayName}</EventTitle>
        {evt.isUserConfigurable ? (
          <ResetButton
            theme={theme}
            onClick={() => setResetConfirm(evt)}
            title="Restore to system defaults"
          >
            Restore Default
          </ResetButton>
        ) : (
          <SystemBadge theme={theme}>System Managed</SystemBadge>
        )}
      </EventHeaderRow>
      <EventDescription theme={theme}>{evt.description}</EventDescription>
      {renderRuleGrid(evt)}
    </EventCard>
  );

  return (
    <>
      <PageContainer>
        <PageHeader>
          <HeaderRow>
            <HeaderIcon theme={theme}><Bell size={22} /></HeaderIcon>
            <div>
              <ThemedText type="title" style={{ fontSize: '22px', fontWeight: 700, marginBottom: 2 }}>
                Notification Events
              </ThemedText>
              <PageDescription theme={theme}>
                Control who receives notifications for each event and channel.
              </PageDescription>
            </div>
          </HeaderRow>

          {!loading && events.length > 0 && (
            <HeaderStats>
              <StatBadge theme={theme}>
                <StatDot $color="#59caee" />
                {stats.total} events
              </StatBadge>
              <StatBadge theme={theme}>
                <StatDot $color="#22c55e" />
                {stats.configurable} configurable
              </StatBadge>
              <StatBadge theme={theme}>
                <StatDot $color="#f59e0b" />
                {stats.categories} categories
              </StatBadge>
            </HeaderStats>
          )}
        </PageHeader>

        {loading ? (
          <LoadingSkeleton theme={theme} />
        ) : events.length === 0 ? (
          <EmptyState theme={theme}>
            <EmptyIcon><Bell size={48} /></EmptyIcon>
            <ThemedText type="default" style={{ color: theme.colors.muted }}>
              No notification events configured yet.
            </ThemedText>
          </EmptyState>
        ) : (
          sortedCategories.map((category, idx) => {
            const meta = CATEGORY_META[category] || { icon: <Bell size={18} />, color: '#6b7280', label: category };
            return (
              <CategorySection key={category} $delay={`${idx * 0.08}s`}>
                <CategoryHeader theme={theme}>
                  <CategoryIcon $color={meta.color}>{meta.icon}</CategoryIcon>
                  <CategoryTitle theme={theme}>{meta.label}</CategoryTitle>
                  <CategoryCount theme={theme} $color={meta.color}>
                    {grouped[category].length}
                  </CategoryCount>
                </CategoryHeader>
                {grouped[category].map(evt => renderEventCard(evt, meta.color))}
              </CategorySection>
            );
          })
        )}
        {resetConfirm && (
          <ConfirmOverlay onClick={() => !resetting && setResetConfirm(null)}>
            <ConfirmDialog theme={theme} onClick={e => e.stopPropagation()}>
              <ConfirmTitle theme={theme}>Restore to Default</ConfirmTitle>
              <ConfirmText theme={theme}>
                Reset "{resetConfirm.displayName}" notification rules back to system defaults?
                Any custom settings for this event will be removed.
              </ConfirmText>
              <ConfirmActions>
                <ConfirmCancelBtn
                  theme={theme}
                  onClick={() => setResetConfirm(null)}
                  disabled={resetting}
                >
                  Cancel
                </ConfirmCancelBtn>
                <ConfirmResetBtn
                  theme={theme}
                  onClick={() => handleResetToDefault(resetConfirm.eventType)}
                  disabled={resetting}
                >
                  {resetting ? 'Restoring...' : 'Restore'}
                </ConfirmResetBtn>
              </ConfirmActions>
            </ConfirmDialog>
          </ConfirmOverlay>
        )}
      </PageContainer>
    </>
  );
};

export const NotificationEventsPage = () => (
  <Layout currentPath="/notification-events">
    <NotificationEventsSection />
  </Layout>
);
